import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Target, Eye, BookOpen, Award, Search, MessageSquare, Users, Stethoscope, Activity, Building2, LayoutDashboard, FlaskConical, Hospital, PlusSquare, Pill, Microscope, AlertCircle, Clock, Crosshair, Network, CheckCircle2, Zap, BarChart, Cpu, Settings, Rocket, Monitor, Lightbulb, TrendingUp, ShieldCheck, CheckSquare, FileText, Database, Video, MessageCircle, Mic, FileCheck, BarChart2, Footprints, Calendar, Mail, ShieldAlert, Layers, HelpCircle } from 'lucide-react';
import { Question, FormState, OtherTextState } from './types';
import { IntroScreen } from './IntroScreen';
import { QuestionScreen } from './QuestionScreen';
import { SummaryScreen } from './SummaryScreen';
import { ProgressBar } from './ProgressBar';
const QUESTIONS: Question[] = [{
  id: 'objective',
  headline: 'What is the primary objective for San Diego?',
  type: 'ranking',
  options: [{
    id: 'leads',
    label: 'Generate qualified leads',
    icon: Target
  }, {
    id: 'awareness',
    label: 'Increase awareness of Minerva',
    icon: Eye
  }, {
    id: 'educate',
    label: 'Educate the market about your technology',
    icon: BookOpen
  }, {
    id: 'credibility',
    label: 'Strengthen scientific credibility',
    icon: Award
  }, {
    id: 'research',
    label: 'Conduct market research',
    icon: Search
  }, {
    id: 'sales',
    label: 'Support sales conversations',
    icon: MessageSquare
  }],
  reward: 'Understanding your priorities helps us design the right Snak structure.'
}, {
  id: 'audience',
  headline: 'Who are the most important audiences to influence?',
  type: 'multi',
  hasOther: true,
  options: [{
    id: 'oncologists',
    label: 'Oncologists',
    icon: Stethoscope
  }, {
    id: 'radiologists',
    label: 'Radiologists',
    icon: Activity
  }, {
    id: 'nuclear',
    label: 'Nuclear medicine specialists',
    icon: FlaskConical
  }, {
    id: 'admins',
    label: 'Hospital administrators',
    icon: Building2
  }, {
    id: 'directors',
    label: 'Imaging center directors',
    icon: LayoutDashboard
  }, {
    id: 'researchers',
    label: 'Researchers',
    icon: Users
  }, {
    id: 'other',
    label: 'Other',
    icon: PlusSquare
  }],
  reward: 'The audience determines both messaging and poll questions.'
}, {
  id: 'icp',
  headline: 'Which organizations are your primary targets?',
  subtitle: 'Select one or two',
  type: 'max-2',
  hasOther: true,
  options: [{
    id: 'academic',
    label: 'Academic hospitals',
    icon: Building2
  }, {
    id: 'cancer_centers',
    label: 'Large cancer centers',
    icon: Hospital
  }, {
    id: 'private_clinics',
    label: 'Private imaging clinics',
    icon: PlusSquare
  }, {
    id: 'pharma',
    label: 'Pharma research collaborations',
    icon: Pill
  }, {
    id: 'cro',
    label: 'Clinical research organizations',
    icon: Microscope
  }, {
    id: 'other',
    label: 'Other',
    icon: PlusSquare
  }],
  reward: 'This helps us tailor the messaging and positioning.'
}, {
  id: 'problem',
  headline: 'What is the biggest challenge in the market today?',
  type: 'single',
  options: [{
    id: 'trust',
    label: 'Building trust for large contracts',
    icon: ShieldAlert
  }, {
    id: 'size_risk',
    label: 'Being perceived as too small or risky',
    icon: AlertCircle
  }, {
    id: 'differentiation',
    label: 'Differentiating from other specialists',
    icon: Crosshair
  }, {
    id: 'platform_proof',
    label: 'Proving full platform capabilities',
    icon: Layers
  }, {
    id: 'procurement',
    label: 'Navigating complex procurement',
    icon: Network
  }],
  reward: 'This defines the problem framing in the quiz teaser.'
}, {
  id: 'uniqueness',
  headline: 'Where does Minerva create the strongest value?',
  type: 'multi',
  hasOther: true,
  options: [{
    id: 'full_platform',
    label: 'Full platform delivery',
    icon: Layers
  }, {
    id: 'accuracy',
    label: 'Improved diagnostic accuracy',
    icon: CheckCircle2
  }, {
    id: 'speed',
    label: 'Faster imaging analysis',
    icon: Zap
  }, {
    id: 'monitoring',
    label: 'Better treatment monitoring',
    icon: Activity
  }, {
    id: 'quantitative',
    label: 'Advanced quantitative imaging',
    icon: BarChart
  }, {
    id: 'ai',
    label: 'AI-driven interpretation',
    icon: Cpu
  }, {
    id: 'other',
    label: 'Other',
    icon: PlusSquare
  }],
  reward: 'This determines the solution reveal in the Trailer Snak.'
}, {
  id: 'competitors',
  headline: 'Who do you compete with most often?',
  type: 'multi',
  hasOther: true,
  options: [{
    id: 'sofie',
    label: 'SOFIE',
    icon: Building2
  }, {
    id: 'radiomedix',
    label: 'Radiomedix',
    icon: Building2
  }, {
    id: 'large_pharma',
    label: 'Large Pharma (Internal)',
    icon: Building2
  }, {
    id: 'other',
    label: 'Other',
    icon: PlusSquare
  }],
  reward: 'This helps us build competitive positioning and warfare mapping.'
}, {
  id: 'perception',
  headline: 'How is Minerva currently perceived in the market?',
  type: 'single',
  options: [{
    id: 'large_safer',
    label: 'Large players are seen as safer choices',
    icon: ShieldCheck
  }, {
    id: 'specialists_similar',
    label: 'Smaller specialists are viewed as largely similar',
    icon: Users
  }, {
    id: 'science_unrecognized',
    label: 'Scientific strengths are not fully recognized',
    icon: HelpCircle
  }, {
    id: 'imaging_only',
    label: 'Strong in imaging, but not seen as a full platform',
    icon: Eye
  }],
  reward: 'This determines whether the congress Snaks should focus on: Awareness, Understanding, or Trust.'
}, {
  id: 'assets',
  headline: 'Which assets do you already have?',
  type: 'multi',
  options: [{
    id: 'publications',
    label: 'Scientific publications',
    icon: FileText
  }, {
    id: 'data',
    label: 'Clinical validation data',
    icon: Database
  }, {
    id: 'videos',
    label: 'Product demo videos',
    icon: Video
  }, {
    id: 'testimonials',
    label: 'Expert testimonials',
    icon: MessageCircle
  }, {
    id: 'presentations',
    label: 'Conference presentations',
    icon: Mic
  }, {
    id: 'case_studies',
    label: 'Case studies',
    icon: FileCheck
  }],
  reward: 'These assets will become reward content inside the Snaks.'
}, {
  id: 'metrics',
  headline: 'How will you measure success at the congress?',
  type: 'multi',
  buttonText: 'Finish',
  options: [{
    id: 'leads',
    label: 'Number of leads generated',
    icon: Users
  }, {
    id: 'participants',
    label: 'Number of poll participants',
    icon: BarChart2
  }, {
    id: 'traffic',
    label: 'Booth traffic',
    icon: Footprints
  }, {
    id: 'meetings',
    label: 'Meetings booked',
    icon: Calendar
  }, {
    id: 'followups',
    label: 'Follow-up requests',
    icon: Mail
  }],
  reward: 'This allows us to design the right engagement funnel.'
}];
export function IntakeForm() {
  const [step, setStep] = useState(0); // 0 = Intro, 1-9 = Questions, 10 = Summary
  const [answers, setAnswers] = useState<FormState>({});
  const [otherTexts, setOtherTexts] = useState<OtherTextState>({});
  const handleStart = () => setStep(1);
  const handleNext = () => {
    if (step < QUESTIONS.length) {
      setStep(s => s + 1);
    } else {
      setStep(QUESTIONS.length + 1); // Go to summary
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setStep(s => s - 1);
    } else {
      setStep(0); // Back to intro
    }
  };
  const handleSubmit = () => {
    console.log('Form submitted:', {
      answers,
      otherTexts
    });
    // In a real app, this would send data to an API
    alert('Thank you! Your answers have been recorded.');
  };
  const handleSelect = (questionId: string, optionId: string, type: string) => {
    setAnswers(prev => {
      const currentSelections = prev[questionId] || [];
      if (type === 'single') {
        return {
          ...prev,
          [questionId]: [optionId]
        };
      }
      if (type === 'multi' || type === 'max-2') {
        if (currentSelections.includes(optionId)) {
          // Remove
          return {
            ...prev,
            [questionId]: currentSelections.filter(id => id !== optionId)
          };
        } else {
          // Add (respecting max-2 limit)
          if (type === 'max-2' && currentSelections.length >= 2) {
            return prev; // Do nothing if already at max
          }
          return {
            ...prev,
            [questionId]: [...currentSelections, optionId]
          };
        }
      }
      if (type === 'ranking') {
        if (currentSelections.includes(optionId)) {
          // Remove from ranking
          return {
            ...prev,
            [questionId]: currentSelections.filter(id => id !== optionId)
          };
        } else {
          // Add to end of ranking
          return {
            ...prev,
            [questionId]: [...currentSelections, optionId]
          };
        }
      }
      return prev;
    });
  };
  const handleOtherTextChange = (questionId: string, text: string) => {
    setOtherTexts(prev => ({
      ...prev,
      [questionId]: text
    }));
  };
  const handleClearRanking = (questionId: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: []
    }));
  };
  const currentQuestionIndex = step - 1;
  const currentQuestion = QUESTIONS[currentQuestionIndex];
  const currentSelections = currentQuestion ? answers[currentQuestion.id] || [] : [];
  const currentOtherText = currentQuestion ? otherTexts[currentQuestion.id] || '' : '';
  return <div className="w-full max-w-3xl mx-auto px-6 py-12 md:py-20 min-h-screen flex flex-col" data-id="element-2">
      {/* Header Logo (only show on questions and summary) */}
      {step > 0 && <div className="flex justify-center mb-12" data-id="element-3">
          <img src="https://cdn.magicpatterns.com/uploads/b2pL9HJQtwq7Zfd3ViWje5/Screenshot_2026-03-04_at_16.40.29.png" alt="Minerva Imaging Logo" className="h-10 object-contain" data-id="element-4" />
        </div>}

      {/* Progress Bar */}
      {step > 0 && step <= QUESTIONS.length && <ProgressBar currentStep={step} totalSteps={QUESTIONS.length} data-id="element-5" />}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col relative" data-id="element-6">
        <AnimatePresence mode="wait" data-id="element-7">
          {step === 0 && <IntroScreen key="intro" onStart={handleStart} data-id="element-8" />}

          {step > 0 && step <= QUESTIONS.length && currentQuestion && <QuestionScreen key={currentQuestion.id} question={currentQuestion} selections={currentSelections} otherText={currentOtherText} onSelect={optionId => handleSelect(currentQuestion.id, optionId, currentQuestion.type)} onOtherTextChange={text => handleOtherTextChange(currentQuestion.id, text)} onClearRanking={() => handleClearRanking(currentQuestion.id)} onNext={handleNext} onBack={handleBack} data-id="element-9" />}

          {step > QUESTIONS.length && <SummaryScreen key="summary" onSubmit={handleSubmit} data-id="element-10" />}
        </AnimatePresence>
      </div>
    </div>;
}